import { useRouter } from 'next/router';
import { localeLanguageCodes } from 'shared-locales';
import { useAchievementTypes, useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { AchievementCompletionItemsRender } from 'components/achievements';
import { CalendarEventDateString } from 'components/calendar';
import {
  ApiDownloadButton,
  DisplayAttachments,
  DisplayFormattedDate,
  EmptyIndicator,
  FlexContainer,
  LoadingIndicator,
  ModalButton,
  MoreOptions,
  ProductLinkWithIcon,
  TextWithLineBreaks,
  TimeIndicator
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { AttributeDisplayValue } from 'components/common/customAttributes';
import { ProfileCompletionsSubheaderFilters } from '.';

import type { GetUserCompletionsRes } from 'shared-types/responses';
import type { ButtonInterface } from 'components/common/Button';

interface Props {
  isUserPage?: boolean;
}

const ProfileCompletions = ({ isUserPage }: Props) => {
  const { dateNow: now } = useDateNow();

  const {
    language,
    messages,
    pageState: { user },
    serverConfig: {
      analyticsHideProductStartDate,
      productCustomAttributes,
      workloadInDays,
      workloadInDaysHoursPerDay
    },
    theme
  } = useReduxState();

  const router = useRouter();

  const achievementTypes = useAchievementTypes();

  const apiUrl =
    `/admin/users/${user?.id}/completions?` +
    Object.keys(router?.query || {})
      .map((key) => `&${key}=${router.query[key]}`)
      .join('');

  const { data, loading } = useApiData<GetUserCompletionsRes>({
    onlyIf: Boolean(user?.id),
    url: apiUrl
  });

  const completions = data?.completions;

  const types = {
    'calendar-event': messages.eventCalendarEvent,
    course: messages.commonCourse,
    'individual-achievement': messages.individualAchievement,
    path: messages.commonPath
  };

  const filteredCustomAttributes = productCustomAttributes?.filter(
    (attr) => attr.displayInAnalytics
  );

  const infoButtonProps = {
    category: 'lighter',
    iconStyle: { fontSize: '1.1rem' },
    size: 'small',
    style: {
      borderRadius: 20,
      fontSize: '1.2rem',
      fontWeight: 600,
      padding: '3px 9px 4px 7px'
    }
  } as ButtonInterface;

  return (
    <div className="completions">
      <Box
        headerContent={
          <MoreOptions category="lighter" styleContentButtons={false}>
            <ApiDownloadButton
              category="success"
              filename={`${user?.email || user?.username}.xlsx`}
              icon="file-excel"
              isDropdown={true}
              size="header"
              style={{ whiteSpace: 'nowrap' }}
              text={messages.usersDownloadSummary}
              url={`${apiUrl}&exportToExcel=true`}
            />
          </MoreOptions>
        }
        headerContentWrap={true}
        subHeaderContent={<ProfileCompletionsSubheaderFilters />}
        padding={0}
        title={messages.profileCompletionHistory}
        {...(!loading ? { titleCount: completions?.length } : {})}
      >
        {loading ? (
          <LoadingIndicator />
        ) : completions?.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell>{messages.commonTitle}</BoxTableCell>
                <BoxTableCell>{messages.commonType}</BoxTableCell>
                <BoxTableCell>{messages.commonCompleted}</BoxTableCell>
                {!analyticsHideProductStartDate ? (
                  <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                    {messages.commonStartDate}
                  </BoxTableCell>
                ) : null}
                <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                  {messages.completionsCompletionDate}
                </BoxTableCell>
                <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                  {messages.commonExpirationDate}
                </BoxTableCell>
                <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                  {`${messages.excelWorkload} (${messages.completionWorkloadHours})`}
                </BoxTableCell>

                {workloadInDays ? (
                  <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                    {`${
                      messages.excelWorkload
                    } (${messages.commonDays.toLowerCase()})`}
                  </BoxTableCell>
                ) : null}

                {filteredCustomAttributes?.map((attribute) => (
                  <BoxTableCell
                    key={attribute.key}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {attribute.titleData?.[language] || attribute.title}
                  </BoxTableCell>
                ))}

                <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                  {messages.commonCustomFields}
                </BoxTableCell>

                <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                  {messages.commonOtherInformation}
                </BoxTableCell>
              </BoxTableHeader>

              {completions.map((completion, i) => {
                const customFields =
                  completion.type === 'achievement'
                    ? completion.achievement.customFields?.filter(
                        (attribute) =>
                          completion.userAttributes?.[attribute.id] !==
                          undefined
                      )
                    : null;

                return (
                  <BoxTableRow key={i}>
                    <BoxTableCell>
                      <div className="info">
                        {(completion.type === 'course' && !completion.course) ||
                        (completion.type === 'path' && !completion.path) ? (
                          <div className="title">{completion.title}</div>
                        ) : (
                          <ProductLinkWithIcon
                            achievement={
                              completion.type === 'achievement'
                                ? completion.achievement
                                : null
                            }
                            calendarEvent={
                              completion.type === 'calendar-event'
                                ? completion.calendarEvent
                                : null
                            }
                            course={
                              completion.type === 'course'
                                ? completion.course
                                : null
                            }
                            individualAchievement={
                              completion.type === 'individual-achievement'
                                ? completion
                                : null
                            }
                            path={
                              completion.type === 'path'
                                ? completion.path
                                : null
                            }
                            disableLink={!isUserPage}
                            preferredLanguageVersion={
                              completion.type === 'course' && !isUserPage
                                ? completion?.courseLanguageVersion ||
                                  localeLanguageCodes[language]
                                : undefined
                            }
                          >
                            {completion.type === 'calendar-event' && (
                              <CalendarEventDateString
                                startDate={
                                  completion.calendarEvent.startDate as string
                                }
                                endDate={
                                  completion.calendarEvent.endDate as string
                                }
                              />
                            )}
                          </ProductLinkWithIcon>
                        )}
                      </div>
                    </BoxTableCell>

                    <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                      {completion.type === 'achievement'
                        ? achievementTypes[completion.achievement.type].title
                        : types[completion.type]}
                    </BoxTableCell>

                    <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                      <TimeIndicator
                        language={language}
                        timeString={completion.completionDate as string}
                      />
                    </BoxTableCell>

                    {!analyticsHideProductStartDate ? (
                      <BoxTableCell>
                        {completion.startedAtDate ? (
                          <span className="date-started">
                            <DisplayFormattedDate
                              date={completion.startedAtDate}
                            />
                          </span>
                        ) : null}
                      </BoxTableCell>
                    ) : null}

                    <BoxTableCell>
                      <span
                        className="date-completed"
                        style={
                          completion.completionDate &&
                          Date.parse(completion.completionDate as string) > now
                            ? { color: theme.warningStandaloneColor }
                            : {}
                        }
                      >
                        <DisplayFormattedDate
                          date={completion.completionDate}
                        />
                      </span>
                    </BoxTableCell>

                    <BoxTableCell>
                      {completion.expirationDate ? (
                        <span
                          className={`${
                            Date.parse(completion.expirationDate as string) <
                            now
                              ? 'date-expired'
                              : ''
                          }`}
                        >
                          <DisplayFormattedDate
                            date={completion.expirationDate}
                          />
                        </span>
                      ) : null}
                    </BoxTableCell>

                    <BoxTableCell>
                      {completion.workload
                        ? new Intl.NumberFormat(language).format(
                            completion.workload
                          )
                        : null}
                    </BoxTableCell>

                    {workloadInDays ? (
                      <BoxTableCell>
                        {completion.workload
                          ? new Intl.NumberFormat(language).format(
                              Number(
                                (
                                  completion.workload /
                                  workloadInDaysHoursPerDay
                                ).toFixed(2)
                              )
                            )
                          : null}
                      </BoxTableCell>
                    ) : null}

                    {filteredCustomAttributes?.map((attribute) => (
                      <BoxTableCell
                        key={attribute.key}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        <AttributeDisplayValue
                          value={
                            completion.type === 'achievement'
                              ? completion.achievement?.customAttributes?.[
                                  attribute.key
                                ]
                              : completion.type === 'calendar-event'
                              ? completion.calendarEvent?.customAttributes?.[
                                  attribute.key
                                ]
                              : completion.type === 'course'
                              ? completion.course?.customAttributes?.[
                                  attribute.key
                                ]
                              : completion.type === 'path'
                              ? completion.path?.customAttributes?.[
                                  attribute.key
                                ]
                              : completion.customAttributes?.[attribute.key] ||
                                null
                          }
                        />
                      </BoxTableCell>
                    ))}

                    <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                      {completion.type === 'achievement' &&
                      customFields?.length ? (
                        <FlexContainer style={{ gap: 20 }}>
                          {customFields.map((attribute, attributeIndex) => (
                            <div key={attributeIndex}>
                              <span style={{ fontWeight: 400 }}>
                                {attribute.titleTranslations?.[language] ||
                                  attribute.title}
                                {': '}
                              </span>
                              <AttributeDisplayValue
                                style={{ display: 'inline-block' }}
                                value={completion.userAttributes[attribute.id]}
                              />
                            </div>
                          ))}
                        </FlexContainer>
                      ) : null}
                    </BoxTableCell>

                    <BoxTableCell>
                      <div className="info-details">
                        {(completion.type === 'achievement' ||
                          completion.type === 'individual-achievement') &&
                        completion.comments ? (
                          <ModalButton
                            icon="comment"
                            modalProps={{
                              closeButton: true,
                              title: messages.commonComment,
                              width: 'medium'
                            }}
                            text={messages.commonComment}
                            {...infoButtonProps}
                          >
                            <div className="comment">
                              <TextWithLineBreaks text={completion.comments} />
                            </div>
                          </ModalButton>
                        ) : null}

                        {completion.type === 'individual-achievement' ||
                        (completion.type === 'achievement' &&
                          completion.achievement
                            ?.certificateAllowDownloading) ? (
                          <DisplayAttachments
                            attachments={completion.attachments}
                            isConfidential={
                              completion.type === 'achievement'
                                ? !completion.canSeeAttachments
                                : false
                            }
                            modalButtonProps={infoButtonProps}
                          />
                        ) : null}

                        {completion.type === 'achievement' ? (
                          <AchievementCompletionItemsRender
                            authorized={completion.canSeeCompletionItems}
                            cleared={
                              completion.completionItemsClearedDate as unknown as string
                            }
                            confidential={completion.isConfidential}
                            items={completion.template}
                            renderInModal={true}
                            renderInModalProps={infoButtonProps}
                            targetUserId={user?.id}
                            type={completion.achievement?.type}
                          />
                        ) : null}

                        {completion.type === 'course' &&
                        completion.course?.certificateAllowDownloading ? (
                          <ApiDownloadButton
                            filename={`${completion.title}.pdf`}
                            icon={['far', 'file-pdf']}
                            text={messages.commonCertificate}
                            url={
                              `/admin/certificate` +
                              `?completionId=${completion.id}` +
                              `&language=${language}`
                            }
                            {...infoButtonProps}
                          />
                        ) : null}

                        {completion.type === 'calendar-event' &&
                        completion.calendarEvent
                          ?.certificateAllowDownloading ? (
                          <ApiDownloadButton
                            filename={`${completion.title}.pdf`}
                            icon={['far', 'file-pdf']}
                            text={messages.commonCertificate}
                            url={
                              `/admin/calendar-event-certificate` +
                              `?eventId=${completion.calendarEvent.id}` +
                              `&userId=${completion.user.id}` +
                              `&language=${language}`
                            }
                            {...infoButtonProps}
                          />
                        ) : null}
                      </div>
                    </BoxTableCell>
                  </BoxTableRow>
                );
              })}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator icon="list" title={messages.commonNoData} />
        )}
      </Box>

      <style jsx>{`
        .info {
          padding: 5px 0;
        }
        .title {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 700;
        }
        .info-details {
          display: flex;
          gap: 10px;
        }
        .date-completed {
          color: ${theme.successStandaloneColor};
        }
        .date-expired {
          color: ${theme.dangerColor};
        }
        .comment {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          color: ${theme.textColor};
          font-size: 1.5rem;
          font-weight: 400;
          padding: 10px;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .info {
            min-width: 420px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileCompletions;
